// Angular
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
// Afidium
import { IMenuItem, IPopupMenuConfig } from 'libraries/component/popup-menu/popup-menu.component';
import _cloneDeep from 'lodash-es/cloneDeep';

import { Yii } from "libraries/interfaces/Yii";
import { ILabels } from "libraries/interfaces/ILabels";
import { IDateRangeFieldSpecs } from "libraries/module/formbuilder/fields/date-range/date-range.component";
import { ICheckboxFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";

export enum OptionConfigTypes {
    DateRange = 'DateRange',
}

export interface IDateRangeOptionsConfig {
    label: string;
    optionConfig: IDateRangeOptionConfig[];
}

export interface IDateRangeOptionConfig {
    label: string;
    type: OptionConfigTypes;
    missingDateFieldConfig?: ICheckboxFieldSpecs;
    fieldConfig: IDateRangeFieldSpecs;
    formControl: string;
    active?: boolean;
}

/**
 * Displays multiple `date-range-field`s.
 */
@Component({
    selector: 'date-field-set',
    template: `
        <ul>
            <li class="date-item"
                *ngFor="let option of optionsConfig.optionConfig; trackBy: trackByFn"
                [ngClass]="{ 'hide': !option.active }">
                <date-range-field [formGroup]="form.controls[option.formControl]"
                                  [missingDateFieldConfig]="option.missingDateFieldConfig"
                                  [fieldConfig]="option.fieldConfig"
                                  class="flex-1 e2e-{{option.formControl}}">
                </date-range-field>

                <btnForm class="remove-button"
                         *ngIf="activeItems > 0"
                         styleType="actionLink"
                         (keydown.enter)="removeField(option)"
                         (click)="removeField(option)">
                    <fa-icon [icon]="iconTrash"></fa-icon>
                </btnForm>
            </li>
        </ul>

        <popup-menu class="sep-top"
                    styleType="actionLink"
                    *ngIf="activeItems < optionsConfig.optionConfig.length"
                    icon="fa-plus"
                    [menuConfig]="menuConfig"
                    (onSelect)="addField($event)"></popup-menu>

    `,
    styleUrls: ['./date-field-set.scss']
})

export class DateFieldSetComponent implements OnInit, OnChanges {

    @Input()
    public form: FormGroup;

    @Input()
    public optionsConfig: IDateRangeOptionsConfig;

    public menuConfig: IPopupMenuConfig;
    public activeItems: number = 0;
    public labels: ILabels = {
        remove: Yii.t('wui-sasha-js', "Supprimer le critère de date"),
    };
    public iconTrash: IconDefinition = faTrash;

    private initialOptionsConfig: IDateRangeOptionsConfig;

    public ngOnInit(): void {
        this.initialOptionsConfig = _cloneDeep(this.optionsConfig);
        this.initMenuConfig();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.optionsConfig.previousValue !== changes.optionsConfig.currentValue) {
            this.initialOptionsConfig = _cloneDeep(this.optionsConfig);
            this.initMenuConfig();
        }
    }

    /**
     * @method initMenuConfig
     * @description initiate the menu config object
     */
    public initMenuConfig(): void {
        this.optionsConfig.optionConfig.forEach((config: IDateRangeOptionConfig) => {
            const value = this.form.get(config.formControl).value;
            if (value && (value.missing || value.min || value.max)) {
                config.active = true;
            }
        });

        const menuConfig: IMenuItem[] = this.optionsConfig.optionConfig
            .filter((config: IDateRangeOptionConfig) => !config.active)
            .map((config: IDateRangeOptionConfig) => {
                return {
                    label: config.label,
                    actionName: 'ADD_' + config.formControl.toUpperCase()
                };
            });
        this.menuConfig = {
            buttonLabel: this.optionsConfig.label,
            items: menuConfig,
        };

        this.getActiveItems();
    }

    /**
     * @method addField
     * @description add some targeted field to the list
     */
    public addField(formControlAction: string): void {
        const formControl: string = formControlAction.replace('ADD_', '');
        this.optionsConfig.optionConfig = this.optionsConfig.optionConfig
            .map((config: IDateRangeOptionConfig) => {
                config.active = config.formControl.toUpperCase() === formControl ? true : config.active;
                return config;
            });
        this.initMenuConfig();
    }

    /**
     * @method removeField
     * @description remove a targeted field from the list
     */
    public removeField(option: IDateRangeOptionConfig): void {
        this.optionsConfig.optionConfig = this.optionsConfig.optionConfig
            .map((config: IDateRangeOptionConfig) => {
                config.active = config.label === option.label ?
                    false :
                    config.active;
                return config;
            });
        this.initMenuConfig();
    }

    public reset(): void {
        if (this.initialOptionsConfig) {
            this.optionsConfig.optionConfig
                .filter((config: IDateRangeOptionConfig) => config.active)
                .forEach((config: IDateRangeOptionConfig) => this.removeField(config));

            this.optionsConfig = _cloneDeep(this.initialOptionsConfig);
            this.initMenuConfig();
        }
    }
    public trackByFn(index: number): number {
        return index;
    }

    private getActiveItems(): void {
        let count: number = 0;
        this.optionsConfig.optionConfig
            .forEach((config: IDateRangeOptionConfig) => {
                if (config.active) {
                    count = count + 1;
                }
            });
        this.activeItems = count;
    }
}
